import { Fragment, KeyboardEvent, useMemo, useRef, useState } from 'react';
import { Combobox, Transition } from '@headlessui/react';
import { ChevronDownIcon, XIcon } from '@heroicons/react/outline';
import classNames from 'classnames';
import Tag from '../svgs/Tag';
import { getTagStyles } from '../lib/colors';
import ProgramTagChip from './ProgramTag/ProgramTagChip';

export type MultiPickerOption = {
  id: string;
  name: string;
  /** Tag colour; when set, the option renders as a program tag chip */
  color?: string;
};

type MultiPickerProps = {
  label: string;
  options: MultiPickerOption[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  placeholder: string;
};

const MultiPicker = ({
  label,
  options,
  selectedIds,
  onChange,
  placeholder,
}: MultiPickerProps) => {
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const selected = useMemo(
    () => options.filter((option) => selectedIds.includes(option.id)),
    [options, selectedIds],
  );
  const available = useMemo(
    () =>
      options.filter(
        (option) =>
          !selectedIds.includes(option.id) &&
          option.name.toLowerCase().includes(query.trim().toLowerCase()),
      ),
    [options, selectedIds, query],
  );

  const remove = (id: string) => onChange(selectedIds.filter((x) => x !== id));

  // Backspace in an empty search removes the last selection, like a tags field.
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && query === '' && selected.length > 0) {
      remove(selected[selected.length - 1].id);
    }
  };

  return (
    <div className="flex w-full flex-col gap-y-2">
      <div className="font-sans text-caption font-medium text-neutral-150">
        {label}
      </div>
      <Combobox
        as="div"
        className="relative w-full text-body"
        value={null}
        onChange={(option: MultiPickerOption | null) => {
          if (option) {
            onChange([...selectedIds, option.id]);
          }
        }}
        immediate
      >
        {/* Selections sit in the field itself, with the search input after them. */}
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <div
          role="group"
          aria-label={label}
          className="relative flex min-h-[52px] w-full cursor-text flex-wrap items-center gap-2 rounded-md border border-neutral-100 bg-white py-2 pl-3 pr-9 shadow-sm focus-within:ring-2 focus-within:ring-secondary-50"
          onClick={() => inputRef.current?.focus()}
        >
          {selected.map((option) =>
            option.color ? (
              <button
                key={option.id}
                type="button"
                className="inline-flex max-w-full items-center gap-2 rounded-full px-3 py-1 text-small-caption"
                style={getTagStyles(option.color)}
                aria-label={`Remove ${option.name}`}
                onClick={(event) => {
                  event.stopPropagation();
                  remove(option.id);
                }}
              >
                <Tag className="h-4 w-4 min-w-[1rem]" />
                <span className="max-w-[200px] truncate">{option.name}</span>
                <XIcon
                  className="h-3 w-3 text-neutral-125"
                  aria-hidden="true"
                />
              </button>
            ) : (
              <button
                key={option.id}
                type="button"
                className="inline-flex max-w-full items-center gap-2 rounded-full bg-neutral-50 px-3 py-1 text-small-caption text-neutral-150"
                aria-label={`Remove ${option.name}`}
                onClick={(event) => {
                  event.stopPropagation();
                  remove(option.id);
                }}
              >
                <span className="max-w-[200px] truncate">{option.name}</span>
                <XIcon
                  className="h-3 w-3 text-neutral-125"
                  aria-hidden="true"
                />
              </button>
            ),
          )}
          <Combobox.Input
            ref={inputRef}
            aria-label={label}
            className={classNames(
              'min-w-[6rem] flex-1 border-0 bg-transparent p-0 text-left text-caption text-secondary-150',
              'placeholder:text-neutral-110 focus:outline-none focus:ring-0',
            )}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={selected.length === 0 ? placeholder : undefined}
          />
          <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronDownIcon
              className="h-5 w-5 text-neutral-125"
              aria-hidden="true"
            />
          </Combobox.Button>
        </div>
        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          afterLeave={() => setQuery('')}
        >
          <Combobox.Options className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-neutral-75 bg-white py-1 text-caption shadow-lg focus:outline-none">
            {available.length === 0 ? (
              <div className="py-3 px-5 text-neutral-110">No matches</div>
            ) : (
              available.map((option) => (
                <Combobox.Option key={option.id} value={option}>
                  {({ active }) => (
                    <div
                      className={classNames(
                        'cursor-pointer select-none py-3 px-5 text-green-150',
                        { 'bg-neutral-25': active },
                      )}
                    >
                      {option.color ? (
                        <ProgramTagChip
                          tag={{ name: option.name, color: option.color }}
                        />
                      ) : (
                        option.name
                      )}
                    </div>
                  )}
                </Combobox.Option>
              ))
            )}
          </Combobox.Options>
        </Transition>
      </Combobox>
    </div>
  );
};

export default MultiPicker;
